import React from "react";
import {
  ArrowRight,
  Mic,
  MessageSquare,
  Smartphone,
  Calendar,
  FileText,
  Sparkles,
} from "lucide-react";

const WelcomeScreen = ({ onStart, hasStarted }) => {
  return (
    <div className="min-h-screen gradient-bg py-10 px-4 page-transition flex items-center justify-center">
      <div className="max-w-2xl mx-auto w-full animate-fadeIn">
        {/* Main Card */}
        <div className="glass rounded-3xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="px-8 pt-10 pb-8 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-gradient-to-r from-adhd-yellow/10 to-adhd-orange/10 border border-adhd-yellow/25 rounded-full text-adhd-yellow text-xs font-semibold tracking-wide uppercase mb-8">
              <Sparkles size={13} />
              <span>5-Day Assessment</span>
            </div>

            <h1 className="text-4xl md:text-5xl font-black mb-4 leading-tight">
              <span className="gradient-text">ADHD Harmony</span>
            </h1>

            <p className="text-xl md:text-2xl text-gray-100 font-semibold mb-6">
              Stop fighting your ADHD brain.
              <br />
              Start working with it.
            </p>

            <p className="text-gray-400 leading-relaxed max-w-lg mx-auto">
              This isn&apos;t a quick quiz. Over 5 days, you&apos;ll answer 15
              deep questions that follow your challenge journey - and
              receive a personalized transformation report at the end. Nothing
              generic. Uniquely yours.
            </p>
          </div>

          {/* Divider */}
          <div className="mx-8 h-px bg-gradient-to-r from-transparent via-gray-600/40 to-transparent" />

          {/* How it works */}
          <div className="px-8 py-8">
            <h2 className="text-xs font-semibold tracking-widest uppercase text-gray-500 mb-5">
              How it works
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-xl bg-adhd-yellow/10 border border-adhd-yellow/15 flex-shrink-0 mt-0.5">
                  <Calendar size={16} className="text-adhd-yellow" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-200">
                    3 questions per day
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    5 days, 15 questions total
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="p-2 rounded-xl bg-adhd-yellow/10 border border-adhd-yellow/15 flex-shrink-0 mt-0.5">
                  <Mic size={16} className="text-adhd-yellow" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-200">
                    Type or speak
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Use voice recording if you prefer
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="p-2 rounded-xl bg-adhd-yellow/10 border border-adhd-yellow/15 flex-shrink-0 mt-0.5">
                  <MessageSquare size={16} className="text-adhd-yellow" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-200">
                    AI follow-ups
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Helps you go deeper on your answers
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="p-2 rounded-xl bg-adhd-yellow/10 border border-adhd-yellow/15 flex-shrink-0 mt-0.5">
                  <FileText size={16} className="text-adhd-yellow" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-200">
                    Personal report
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Your patterns, potential &amp; path forward
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="mx-8 h-px bg-gradient-to-r from-transparent via-gray-600/40 to-transparent" />

          {/* CTA Section */}
          <div className="px-8 pt-8 pb-6">
            <button
              onClick={onStart}
              className="w-full premium-button text-black rounded-2xl py-4 px-8 font-bold text-lg hover-lift flex items-center justify-center gap-3"
            >
              <span>{hasStarted ? "Continue Your Assessment" : "Start Your Assessment"}</span>
              <ArrowRight size={20} />
            </button>
          </div>

          {/* Local storage notice */}
          <div className="px-8 pb-6">
            <div className="flex items-start gap-2.5 px-4 py-3 rounded-xl bg-gray-800/40 border border-gray-700/30">
              <Smartphone
                size={14}
                className="text-gray-500 flex-shrink-0 mt-0.5"
              />
              <p className="text-xs text-gray-500 leading-relaxed">
                Your answers are saved in your browser&apos;s local storage.
                Use the <strong className="text-gray-400">same device</strong>{" "}
                and <strong className="text-gray-400">same browser</strong> to
                continue where you left off.
              </p>
            </div>
          </div>

          {/* Legal */}
          <div className="px-8 pb-8">
            <p className="text-center text-xs text-gray-600">
              By starting you agree with the{" "}
              <a
                href="https://adhd-harmony.webflow.io/legal/ai-assessment-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-500 hover:text-adhd-yellow underline transition-colors"
              >
                Terms of Use
              </a>
              ,{" "}
              <a
                href="https://adhd-harmony.webflow.io/legal/ai-assessment-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-500 hover:text-adhd-yellow underline transition-colors"
              >
                Privacy Policy
              </a>
              , and{" "}
              <a
                href="https://adhd-harmony.webflow.io/legal/ai-assessment-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-500 hover:text-adhd-yellow underline transition-colors"
              >
                Cookie Policy
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WelcomeScreen;
